import type { FabCorner } from '@/hooks/use-fab-corner'
import { fabCornerTransformFor } from '@/hooks/use-fab-sidebar-inset'
import type { TextDirection } from '@/lib/i18n/direction'

/**
 * Where the activity dock rests: with the Feedback launcher, never on it.
 *
 * The launcher (FabStackHost / FeedbackFab) is a 48 px circle that rests 1.5rem in from a corner,
 * steps to the bottom corner at the inline start while a dialog or sheet is open or on a creation
 * route, keeps off the sidebar, and rises above a page's bottom action bar. The dock follows the
 * SAME corner by the same rules (it is handed the launcher's effective corner, the sidebar inset
 * and the bar lift the host computes), so the two always stay on one side of the window and where
 * the launcher goes the dock goes. This is how the dock joins the corner reservation that
 * `body[data-fab-reserve]` keeps for the launcher (globals.css): it never asks a table for more
 * room, because it rests ABOVE the launcher, clear of the pagination footer a table pads for it.
 *
 * At a bottom corner the dock sits ABOVE the launcher AND its hover handles: the handle that moves the
 * launcher vertically floats 60 px above it and is 24 px tall (FeedbackFab), so its top is 84 px (5.25rem)
 * over the launcher's bottom edge. The dock's bottom edge rests 5.5rem over that edge: clear of the
 * launcher and of the handle whenever it shows (the one beside the launcher is out of the dock's column).
 * At a top corner the launcher is out of the way, so the dock takes the bottom corner on that side.
 */

/** How far above the launcher's bottom edge the dock rests: the launcher (3rem), its handle (to 5.25rem) and a gap. */
export const DOCK_RISE = '5.5rem'

export function dockTransformFor(
  fabCorner: FabCorner,
  sidebarInsetPx: number,
  direction: TextDirection = 'ltr',
  liftAboveActions: boolean = false,
  barLiftPx: number | null = null,
): string {
  const side = fabCorner.endsWith('right') ? 'right' : 'left'
  const atBottom = fabCorner.startsWith('bottom')
  const corner: FabCorner = side === 'right' ? 'bottom-right' : 'bottom-left'
  // The same corner maths as the launcher; the lift applies only where the launcher itself is at the bottom.
  const base = fabCornerTransformFor(corner, sidebarInsetPx, direction, atBottom && liftAboveActions, atBottom ? barLiftPx : null)
  return atBottom ? `${base} translateY(-${DOCK_RISE})` : base
}
